import React from 'react';
import { motion } from 'motion/react';

export default function PrivacyPolicy() {
  return (
    <div className="pt-40 pb-24 min-h-screen bg-black text-white px-4 sm:px-8 lg:px-16 relative overflow-hidden">
      {/* Background Gradients */}
      <div className="absolute inset-0 w-full h-full z-0 pointer-events-none">
        <div className="absolute inset-0 bg-gradient-to-b from-black via-black/90 to-black pointer-events-none" />
      </div>

      {/* Background glow */}
      <div className="absolute top-1/4 left-1/4 w-[500px] h-[500px] bg-blue-500/5 rounded-full blur-[120px] pointer-events-none z-0" />
      <div className="absolute bottom-1/4 right-1/4 w-[500px] h-[500px] bg-purple-500/5 rounded-full blur-[120px] pointer-events-none z-0" />

      <div className="max-w-4xl mx-auto relative z-10">
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          animate={{ opacity: 1, y: 0 }}
          className="mb-16"
        >
          <div className="liquid-glass rounded-full px-4 py-1.5 inline-block mb-6">
            <span className="text-sm font-medium text-white font-body tracking-wide">
              Legal
            </span>
          </div>
          <h1 className="text-5xl md:text-6xl font-heading italic tracking-tight leading-[0.9] mb-6">
            Privacy Policy
          </h1>
          <p className="text-white/60 font-body font-light text-lg max-w-2xl leading-relaxed">
            Effective Date: {new Date().toLocaleDateString('en-GB', { day: 'numeric', month: 'long', year: 'numeric' })}
          </p>
        </motion.div>

        <motion.div 
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          transition={{ delay: 0.2 }}
          className="liquid-glass rounded-[2rem] p-8 md:p-12 border border-white/10 shadow-[0_20px_50px_rgba(0,0,0,0.5)] prose prose-invert prose-sm sm:prose-base max-w-none font-body font-light leading-relaxed text-white/80"
        >
          <div className="space-y-10">
            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">1. Introduction</h2>
              <p>Welcome to ESYAI LTD (“we”, “our”, “us”). We are committed to protecting your personal data and your privacy.</p>
              <p>This Privacy Policy explains how we collect, use, and safeguard your information when you visit our website.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">2. Company Details</h2>
              <p>
                ESYAI LTD<br />
                Registered in the UK<br />
                Address: Woolwich City Centre, London SE18
              </p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">3. Information We Collect</h2>
              <p>We may collect the following types of data:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Personal Identification Information (name, email address, phone number)</li>
                <li>Communication Data (messages sent via forms)</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">4. How We Use Your Data</h2>
              <p>We use your data to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Provide and improve our services</li>
                <li>Respond to enquiries</li>
                <li>Send updates or marketing (only with consent)</li>
                <li>Ensure website security</li>
                <li>Comply with legal obligations</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">5. Legal Basis for Processing</h2>
              <p>We process your data under:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Consent</li>
                <li>Contractual necessity</li>
                <li>Legal obligations</li>
                <li>Legitimate interests</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">6. Cookies</h2>
              <p>We may use cookies to improve user experience. You can control cookies through your browser settings.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">7. Data Sharing</h2>
              <p>We do not sell your data. We may share it with:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Service providers (hosting, analytics)</li>
                <li>Legal authorities if required</li>
              </ul>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">8. Data Retention</h2>
              <p>We retain your data only as long as necessary for the purposes outlined.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">9. Your Rights (UK GDPR)</h2>
              <p>You have the right to:</p>
              <ul className="list-disc pl-5 space-y-2">
                <li>Access your data</li>
                <li>Correct inaccurate data</li>
                <li>Request deletion</li>
                <li>Restrict processing</li>
                <li>Data portability</li>
                <li>Withdraw consent</li>
              </ul>
              <p className="mt-4">To exercise your rights, contact us at: <a href="mailto:info@esyai.co.uk" className="text-white underline underline-offset-4 decoration-white/20 hover:decoration-white transition-all">info@esyai.co.uk</a></p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">10. Data Security</h2>
              <p>We implement appropriate security measures to protect your data.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">11. Third-Party Links</h2>
              <p>Our website may contain links to external websites. We are not responsible for their privacy practices.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">12. Changes to This Policy</h2>
              <p>We may update this policy periodically. Updates will be posted on this page.</p>
            </section>

            <section>
              <h2 className="text-2xl font-heading italic text-white mb-4">13. Contact Us</h2>
              <p>If you have questions, contact:</p>
              <p>Email: <a href="mailto:info@esyai.co.uk" className="text-white underline underline-offset-4 decoration-white/20 hover:decoration-white transition-all">info@esyai.co.uk</a></p>
            </section>
          </div>
        </motion.div>
      </div>
    </div>
  );
}
